import { Component, Input, OnInit, OnChanges, OnDestroy, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MarkdownService } from 'ngx-markdown';

import * as $ from 'jquery';
import * as _ from 'lodash-es';

import { StateService } from '@uirouter/angular';
import { PollService } from '../../../services/core.poll.service';
import { MenuService } from '../../../../widgets/mega-menu/mega-menu.service.component';
import { NotificationService } from '../../../services/core.notification.service';
import { UiService } from '../../../services/core.ui.service';
import { GfTranslateService } from '../../../services/core.translate.service';
import { UserRest } from '../../../services/core.user.rest.service';
import { UserService } from '../../../services/core.user.service';
import { FeatureFlagService } from '../../../services/core.feature-flags.service';
import { looksLikeHtml } from '../../../services/markdown-content.functions';
import { AccessDeniedService } from '../../../services/core.access-denied.service';
import { ChatStatusService } from '../../../services/core.chat-status.service';

@Component({
    selector: 'topMenu',
    template: require('./topmenu.main.html')
})
export class TopMenuComponent implements OnInit, OnChanges, OnDestroy {

    @Input() siteConfig: any = null;
    @Input() project: any = null;
    @Input() projectMenu: any = null;

    protected currentUser: any = null;
    protected profile: any = {};
    protected options: any = {};
    protected chatButtonTitle = '';
    private chatPermission: any = null;
    protected wall: any = null;
    protected showChatIcon = false;
    private chatActive = null;
    protected chatVisible = null;
    protected chatCount = 0;
    protected hasUser = false;

    private hasProjectPermissions = false;
    private isProjectMember = false;
    private lastSeenWallMessage = 0;
    protected newest = 0;
    protected isMenuOpen = false;

    constructor(
        private window: Window,
        public stateService: StateService,
        private pollService: PollService,
        private menuService: MenuService,
        private notificationService: NotificationService,
        private uiService: UiService,
        private userService: UserService,
        private userRest: UserRest,
        private gfTranslateService: GfTranslateService,
        public featureFlagService: FeatureFlagService,
        private accessDeniedService: AccessDeniedService,
        private markdownService: MarkdownService,
        private sanitizer: DomSanitizer,
        public chatStatusService: ChatStatusService
    ) { }

    ngOnInit() {
        this.currentUser = this.userService.session.getUser();
        this.hasUser = this.userService.session.hasUser();

        if (this.hasUser) {
            this.pollService.add('wall', 'user', this.currentUser.id, 0);
            this.chatStatusService.load();
        }

        this.profile = this.userService.profile;  // passthrough for language
        this.options = this.userService.options;  // passthrough for featurelevel, stickytop, etc.

        this.pollService.subscribe('topmenu', this.handlePollData);
        this.notificationService.subscribe('topmenu', 'HOTKEY', this.handleHotkey);
        this.notificationService.subscribe('topmenu', 'EMBEDDED_CHAT_STATE', this.handleChatState);

        this.menuService.setMenuData(this.project);
        this.setChatAvailable();
    }

    ngOnChanges(changes) {
        this.currentUser = this.userService.session.getUser();
        this.hasUser = this.userService.session.hasUser();

        this.menuService.setMenuData(this.project);
        this.setChatAvailable();
    }

    ngOnDestroy(): void {
        this.pollService.unsubscribe('topmenu');
        this.notificationService.unsubscribe('topmenu');
    }

    menuClick = ($ev) => {
        this.isMenuOpen = !this.isMenuOpen;
    }

    handleChatState = (args) => {
        if (args.source == 'topmenu') return;

        this.setChatStatus(args.state);
    };

    handleHotkey = ($event) => {
        if ($event.shiftKey === true && $event.ctrlKey === true &&
            $event.keyCode === 83) {
            if (!$event.metaKey && !$event.altKey) {
                $event.stopPropagation();
                $event.preventDefault();
                $('#navbar-search-box').focus();
            }
        }
    };

    handlePollData = (args) => {
        // PollService stops for good on 401/403 and broadcasts `response: false`. The top
        // menu is the one component always mounted, so it is where the main app notices
        // that the session died while the user sat on a page. See docs/PERMISSION-REDIRECT.md.
        if (args && args.response === false) {
            const status = args?.error?.status ?? null;
            if (status === 401 || status === 403) {
                this.accessDeniedService.checkExpiredSession().then((expired) => {
                    // False alarm: the session is fine, so that refusal was the poll
                    // endpoint's own answer. handlePollError has already parked
                    // PollService in 'idle' for good, outside the backoff retry path,
                    // and only a fresh login restarts it — so notifications would stay
                    // dead for the rest of the session. Force, because 'idle' can still
                    // be holding a stale in-flight request that plain run() will not
                    // clear.
                    if (!expired) this.pollService.run(true);
                });
            }
            return;
        }

        const wallPosts = _.filter(_.get(args, 'response.items', []), { type: 'wall' })
            .flatMap((result) => _.get(result, 'items', []));

        if (wallPosts.length == 0) {
            return;
        }

        // If we have 5 or fewer individual notifications, then show them.
        // Otherwise, show a summary message
        if (wallPosts.length > 5) {
            this.uiService.message.showInfo("You have " + wallPosts.length + " new notifications", "New Notifications");
        } else {
            wallPosts.forEach((post) => {
                this.uiService.message.showInfo(this.toastText(post.comment), _.get(post, 'user.unixName', 'GForge'),
                    {
                        onClickCallback: () => {
                            this.uiService.state.goToItem(post.itemSection, post.itemRefId, null, post?.project?.unixName);
                        }
                    }
                );
            });
        }
    };

    /**
     * Body text for a wall-post growl.
     *
     * The growl hands its message to bootstrap-notify, which inserts it as
     * HTML. Legacy wall posts carry backend-sanitised HTML and are left alone
     * so they look as they do today. Markdown ones are stored raw, so they are
     * compiled the same way gf-markdown-viewer does and then sanitised, which
     * is the trust boundary for inline HTML in the source.
     */
    toastText(comment) {
        var text = comment || '';
        if (!text || looksLikeHtml(text)) {
            return text;
        }
        const compiled = this.markdownService.parse(text, { markedOptions: { gfm: true, breaks: true } });
        const html = this.sanitizer.sanitize(SecurityContext.HTML, typeof compiled === 'string' ? compiled : '') || '';
        return '<div class="wall-toast">' + html + '</div>';
    };

    moduleAvailable(pm, moduleName) {
        if (!_.get(pm, moduleName, false)) {
            return false;
        }
        if (!_.includes(_.get(pm, 'modules', []), moduleName)) {
            return false;
        }

        return true;
    };

    optionButtonClass(isActive, activeClass?, inactiveClass?) {
        var active = 'btn-primary';
        if (undefined != activeClass) {
            active = activeClass;
        }

        var inactive = 'btn-default';
        if (undefined != inactiveClass) {
            inactive = inactiveClass;
        }

        return isActive ? active : inactive;
    };

    openSAChat() {
        var url = "/chat";
        var target = "_gfStandaloneChat";
        var options = "fullscreen=no,menubar=no,resizable=yes,location=no,scrollbars=no,status=no,toolbar=no,width=1300,height=800";
        var saWindow = this.window.open(url, target, options);
        saWindow.focus();
    };

    logout() {
        this.userService.session.logout()
            .then(
                (response) => {
                    var msgTitle = this.gfTranslateService.translate('Logged out');
                    var msgDescription = this.gfTranslateService.translate('You have been logged out of GForge');
                    this.uiService.message.showSuccess(msgDescription, msgTitle);
                }, () => {
                    var msgErrorTitle = this.gfTranslateService.translate('Logout error');
                    var msgErrorDescription = this.gfTranslateService.translate('There was a problem trying to log you out of GForge');
                    this.uiService.message.showError(msgErrorDescription, msgErrorTitle);
                }
            ).finally(() => {
                // TODO
                this.stateService.go('home', {}, { reload: true });
            });
    };

    toggleChat() {
        if (!this.chatActive || !this.chatVisible) {
            this.notificationService.notifyAll('EMBEDDED_CHAT_STATE', { state: 'visible', source: 'topmenu' });
            this.setChatStatus('visible');
        } else {
            this.notificationService.notifyAll('EMBEDDED_CHAT_STATE', { state: 'hidden', source: 'topmenu' });
            this.setChatStatus('hidden');
        }
    };

    alreadySeenMessage(wallpost) {
        if (wallpost.id > this.lastSeenWallMessage)
            return false;
        return true;
    };

    getWallInfo() {
        if (this.hasUser) {
            this.loadWallMessages();
        }
    };

    goToItem(wallpost) {
        if (wallpost.itemSection && wallpost.itemRefId)
            this.uiService.state.goToItem(wallpost.itemSection, wallpost.itemRefId, null, wallpost?.project?.unixName);
    };

    showProjectLabel(wallpost) {
        if (_.has(wallpost, 'project') && _.has(wallpost.project, 'name')) {
            return 'in ' + wallpost.project.name;
        }
        return '';
    };

    /** 8. INTERNAL FUNCTIONS
     For truly internal needs (not accessible from the templates). **/
    getChatPermission(projectMenu, project) {

        if (!projectMenu || !project) return false;
        if (_.get(projectMenu, 'admin', false)) return true;
        if (_.get(projectMenu, 'forum', 0) == 99) return true;

        var chatId = _.get(project, 'rel.chat.id', 0);
        var perm = _.get(projectMenu, 'forums.' + chatId, 0);
        return perm > 0;
    }

    setChatAvailable() {
        this.chatPermission = this.getChatPermission(this.projectMenu, this.project);
        this.showChatIcon = this.chatPermission;

        if (this.currentUser) {
            this.getProjectPermissions(this.currentUser.unixName, _.get(this.project, 'unixName', false));
        }
    }

    setChatStatus(state) {
        if (this.showChatIcon) {
            if (state == 'visible') {
                this.chatActive = true;
                this.chatVisible = true;
                this.chatCount = 0;
            } else if (state == 'hidden') {
                this.chatActive = true;
                this.chatVisible = false;
            } else if (state == 'closed') {
                this.chatActive = false;
                this.chatVisible = false;
                this.chatCount = 0;
            }
        } else {
            this.chatActive = false;
            this.chatVisible = false;
            this.chatCount = 0;
        }
        this.chatButtonTitle = this.getChatButtonTitle();
    }

    getProjectPermissions(userUnixName, projectUnixName) {
        if ((userUnixName && userUnixName !== 'None') &&
            (projectUnixName && projectUnixName != '')) {

            this.showChatIcon = this.chatPermission;
            // TODO Get rid of this?
            (this.window as any).hasProjectPermissions = this.chatPermission;

            if (!this.chatPermission) {
                this.chatActive = false;
                this.chatVisible = false;
                this.chatCount = 0;
            } else {
                this.chatActive = true;
            }

        } else {
            this.showChatIcon = false;
            (this.window as any).hasProjectPermissions = false;
            this.chatActive = false;
            this.chatVisible = false;
            this.chatCount = 0;
        }
    }

    getChatButtonTitle() {
        switch (this.chatCount) {
            case 0:
                if (this.chatVisible) {
                    return 'Hide chat sidebar';
                } else {
                    return 'Show chat sidebar';
                }
            case 1:
                return 'Show chat sidebar (1 new message)';
            default:
                return 'Show chat sidebar (' + this.chatCount + ' new messages)';
        }
    }

    loadWallMessages() {

        this.wall = null;

        this.userService.preference.getPreference('toaster-since', 0)
            .then((resource) => {
                this.lastSeenWallMessage = resource.preferenceValue;
            },
                (response) => {
                    if (response.status != 404) {
                        var msg = this.gfTranslateService.translate('There was a problem loading wall preference');
                        this.uiService.message.showWarning(msg, 'Data Error');
                    }
                }
            );

        this.userRest.getWall(this.currentUser.unixName)
            .then((resource: any) => {
                var wall = resource.items;
                for (var message in wall) {

                    wall[message].comment = wall[message].comment.length > 300 ? wall[message].comment.slice(0, 300).concat('...') : wall[message].comment;
                    if (wall[message].user) {
                        var userUrl = '<a href="/user/' + wall[message].user.unixName + '" title="Go to this user">' + wall[message].user.unixName + '</a>';
                        wall[message].comment = userUrl + ' ' + wall[message].comment;
                    }

                    var userMatches = wall[message].comment.match(/@(\w+)\b/g);
                    if (!_.isEmpty(userMatches)) {

                        _.each(userMatches, (item) => {

                            var mentionedUser = item.toString().replace('@', '');
                            var mentionedUserUrl = '';

                            if (mentionedUser == this.currentUser.unixName) {
                                mentionedUserUrl = '<a href="/user/' + mentionedUser + '/my" title="Go to my page"  ng-click="$event.stopPropagation();">' + item.toString() + '</a>';
                            } else {
                                mentionedUserUrl = '<a href="/user/' + mentionedUser + '" title="Go to this user"  ng-click="$event.stopPropagation();">' + item.toString() + '</a>';
                            }
                            wall[message].comment = wall[message].comment.split(item).join(mentionedUserUrl);
                        });
                    }

                    if (wall[message].id > this.lastSeenWallMessage)
                        this.newest++;
                }

                this.wall = wall;
                if (this.wall.length > 0) {
                    var lastPreference = this.wall[0].id;
                    this.userService.preference.setPreference('toaster-since', lastPreference);
                }
            },
                () => {
                    var msg = this.gfTranslateService.translate('There was a problem loading wall posts');
                    this.uiService.message.showWarning(msg, 'Data Error');
                });
    }

    setFeatureLevel(name, level) {
        this.featureFlagService.setFeatureLevel(this.userService.getUnixName(), name, level);
    }

}
